import { useCallback, useState } from 'react';
import toNumber from 'lodash/toNumber';
import { type SetSearchParams, useMutableObject } from '../../../lib/hooks';
import { DEFAULT_PAGE, SEARCH_PARAM_KEY_PAGE, SEARCH_PARAM_KEY_PER_PAGE } from '../constants';

const DEFAULT_PER_PAGE = 25;

export type PaginationOptions =
    | {
          // Если true, то не будет менять search параметры
          withoutSearchParams?: boolean;
      }
    | undefined;

export function useDefaultPagination(
    searchParams: URLSearchParams,
    setSearchParams: SetSearchParams,
    resetParams: string[] = [],
    searchParamKeys?: {
        page?: string;
        perPage?: string;
    },
    options?: PaginationOptions
) {
    const { page: searchParamPage = SEARCH_PARAM_KEY_PAGE, perPage: searchParamPerPage = SEARCH_PARAM_KEY_PER_PAGE } =
        searchParamKeys || {};
    const ref = useMutableObject(resetParams);

    // Используются только для случая, когда не нужно менять search параметры
    const [localPage, setLocalPage] = useState(DEFAULT_PAGE);
    const [localPerPage, setLocalPerPage] = useState(DEFAULT_PER_PAGE);

    const setPage = useCallback(
        (page: number) => {
            if (options?.withoutSearchParams) {
                setLocalPage(page);

                return;
            }

            setSearchParams((params) => {
                ref.current.forEach((key) => params.delete(key));

                if (page <= 1) {
                    params.delete(searchParamPage);
                } else {
                    params.set(searchParamPage, page.toString());
                }

                return [params];
            });
        },
        [ref, searchParamPage, setSearchParams, options]
    );

    const setPerPage = useCallback(
        (perPage: number) => {
            if (options?.withoutSearchParams) {
                setLocalPerPage(perPage);

                return;
            }

            setSearchParams((params) => {
                params.delete(searchParamPage);
                ref.current.forEach((key) => params.delete(key));

                if (perPage <= 1) {
                    params.delete(searchParamPerPage);
                } else {
                    params.set(searchParamPerPage, perPage.toString());
                }

                return [params];
            });
        },
        [ref, searchParamPage, searchParamPerPage, setSearchParams, options]
    );

    return {
        page: options?.withoutSearchParams ? localPage : toNumber(searchParams.get(searchParamPage)) || DEFAULT_PAGE,
        setPage,
        perPage: options?.withoutSearchParams
            ? localPerPage
            : toNumber(searchParams.get(searchParamPerPage)) || DEFAULT_PER_PAGE,
        setPerPage,
    };
}
